import { useMemo } from "react";
import { Download, Mail, Printer } from "lucide-react";

import { Pill, PILL_COLORS } from "components/ui/Pill";

import { formatCurrency, formatDate } from "../constants.jsx";

const usePaymentColumns = (
  activeTab,
  { onPay, onDownloadReceipt, onPrintStatement, onSendEmail }
) => {
  const columns = useMemo(() => {
    if (activeTab === "paymentsDue") {
      return [
        {
          id: "dueDate",
          header: "Due Date",
          width: "110px",
          render: (p) => formatDate(p.dueDate),
        },
        {
          id: "service",
          header: "Service",
          width: "minmax(150px, 1fr)",
          render: (p) => p.practiceService?.name || p.serviceName || "-",
        },
        {
          id: "amountDue",
          header: "Amount Due",
          width: "110px",
          render: (p) => <strong>{formatCurrency(p.amountDue)}</strong>,
        },
        {
          id: "consultationDate",
          header: "Consultation Date",
          width: "130px",
          render: (p) => formatDate(p.consultation?.startTime || p.startTime),
        },
        {
          id: "status",
          header: "Status",
          width: "100px",
          render: () => <Pill {...PILL_COLORS.paymentUnpaid}>Unpaid</Pill>,
        },
      ];
    }

    if (activeTab === "patient") {
      return [
        {
          id: "paymentDate",
          header: "Payment Date",
          width: "120px",
          render: (p) => formatDate(p.paymentDate),
        },
        {
          id: "service",
          header: "Service",
          width: "minmax(150px, 1fr)",
          render: (p) => p.practiceService?.name || p.serviceName || "-",
        },
        {
          id: "amountPaid",
          header: "Amount Paid",
          width: "110px",
          render: (p) => <strong>{formatCurrency(p.amountPaid)}</strong>,
        },
        {
          id: "consultationDate",
          header: "Consultation Date",
          width: "130px",
          render: (p) =>
            formatDate(p.consultation?.consultationDate || p.consultationDate),
        },
        {
          id: "status",
          header: "Status",
          width: "90px",
          render: () => <Pill {...PILL_COLORS.paymentPaid}>Paid</Pill>,
        },
      ];
    }

    // family
    return [
      {
        id: "patientName",
        header: "Patient Name",
        width: "minmax(140px, 1fr)",
        render: (p) =>
          `${p.patient?.firstName || ""} ${p.patient?.surname || ""}`.trim() ||
          "-",
      },
      {
        id: "paymentDate",
        header: "Payment Date",
        width: "110px",
        render: (p) => formatDate(p.paymentDate),
      },
      {
        id: "service",
        header: "Service",
        width: "minmax(120px, 1fr)",
        render: (p) => p.practiceService?.name || p.serviceName || "-",
      },
      {
        id: "amountPaid",
        header: "Amount Paid",
        width: "100px",
        render: (p) => <strong>{formatCurrency(p.amountPaid)}</strong>,
      },
      {
        id: "consultationDate",
        header: "Consultation",
        width: "110px",
        render: (p) =>
          formatDate(p.consultation?.consultationDate || p.consultationDate),
      },
      {
        id: "status",
        header: "Status",
        width: "90px",
        render: () => <Pill {...PILL_COLORS.paymentPaid}>Paid</Pill>,
      },
    ];
  }, [activeTab]);

  const rowActions = useMemo(() => {
    if (activeTab === "paymentsDue") {
      return [
        {
          label: "Pay",
          primary: true,
          onClick: (payment) => onPay(payment),
        },
      ];
    }
    return [
      {
        label: "Download Receipt",
        icon: Download,
        primary: true,
        onClick: (payment) => onDownloadReceipt(payment),
      },
    ];
  }, [activeTab, onPay, onDownloadReceipt]);

  // Payments Due has nothing paid yet, so what's being emailed is a statement
  // of what's owed; the other tabs are already-paid transactions, so it's
  // their receipts.
  const attachmentLabel = activeTab === "paymentsDue" ? "Statement" : "Receipts";

  const bulkActions = [
    {
      label: "Print Statement",
      icon: Printer,
      onClick: (selectedItems) =>
        onPrintStatement(selectedItems.map((p) => p.id)),
    },
    {
      label: "Send Email",
      icon: Mail,
      onClick: (selectedItems) =>
        onSendEmail(selectedItems.map((p) => p.id), attachmentLabel),
    },
  ];

  return { columns, rowActions, bulkActions };
};

export { usePaymentColumns };
